import React, { useEffect, useId, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import Icon from '../AppIcon';
import { bloccaScroll } from './scrollLock';

// EAN-13 è il formato principale; gli altri servono per codici SAP / fornitore su etichette 1D comuni
const FORMATI = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
];

// `formati` (opzionale): nomi di Html5QrcodeSupportedFormats, es. ['CODE_128', 'QR_CODE']. Senza: elenco predefinito sopra.
const formatiDaNomi = (nomi) => {
  const lista = Array.isArray(nomi)
    ? nomi.map((n) => Html5QrcodeSupportedFormats[n]).filter((f) => f !== undefined)
    : [];
  return lista.length ? lista : FORMATI;
};

const attesa = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const messaggioErrore = (e) => {
  const nome = e?.name || '';
  const testo = String(e?.message || e || '');
  if (!window.isSecureContext) return 'La fotocamera funziona solo su una connessione sicura (HTTPS).';
  if (nome === 'NotAllowedError' || /permission|denied/i.test(testo)) {
    return 'Permesso alla fotocamera negato. Consenti l\'accesso dalle impostazioni del browser e riprova.';
  }
  if (nome === 'NotFoundError' || /no camera|requested device not found/i.test(testo)) {
    return 'Nessuna fotocamera trovata su questo dispositivo.';
  }
  if (nome === 'NotReadableError') return 'La fotocamera è usata da un\'altra applicazione.';
  return 'Impossibile avviare la fotocamera. Puoi digitare il codice qui sotto.';
};

// Vincoli video: facingMode + risoluzione moderata (i flussi 4K delle camere posteriori dei telefoni
// rallentano la lettura senza migliorarla)
const vincoliVideo = (modo) => ({
  facingMode: modo,
  width: { ideal: 1280 },
  height: { ideal: 720 },
});

/**
 * Scanner di codici a barre con la fotocamera (telefono o PC) + inserimento manuale.
 *
 *  - La fotocamera legge EAN-13 (e EAN-8, UPC-A, Code 128, Code 39); linea rossa animata come uno scanner vero.
 *  - Il pulsante "Cambia" alterna fotocamera posteriore e frontale (facingMode), senza interrogare l'elenco dei
 *    dispositivi con un secondo flusso: su iPhone un secondo accesso alla fotocamera interrompe quello in corso.
 *  - In basso si può digitare il codice e premere Invio: il campo di testo resta sempre libero.
 *  - onRilevato(codice) viene chiamato una sola volta; poi il chiamante chiude lo scanner (onChiudi).
 *
 * Avvio e arresto della fotocamera sono messi in coda (uno alla volta) con una breve pausa tra l'uno e l'altro,
 * per dare al telefono il tempo di rilasciare la fotocamera prima di aprirne un'altra.
 */
const BarcodeScanner = ({ onRilevato, onChiudi, titolo = 'Scansiona codice a barre', formati }) => {
  const idLettore = `lettore-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const [errore, setErrore] = useState(null);
  const [avvio, setAvvio] = useState(true);
  const [modo, setModo] = useState('environment'); // 'environment' = posteriore, 'user' = frontale
  const [piuFotocamere, setPiuFotocamere] = useState(false);
  const [tentativo, setTentativo] = useState(0);
  const [manuale, setManuale] = useState('');

  const rilevato = useRef(false);
  const onRilevatoRef = useRef(onRilevato);
  onRilevatoRef.current = onRilevato;
  const onChiudiRef = useRef(onChiudi);
  onChiudiRef.current = onChiudi;
  const formatiRef = useRef(formati);
  formatiRef.current = formati;

  const lettoreRef = useRef(null); // un'unica istanza di Html5Qrcode per tutta la vita del componente
  const coda = useRef(Promise.resolve()); // operazioni sulla fotocamera, una alla volta

  const accoda = (operazione) => {
    coda.current = coda.current.then(operazione).catch(() => {});
    return coda.current;
  };

  const consegna = (codice) => {
    const pulito = String(codice ?? '').trim();
    if (!pulito || rilevato.current) return;
    rilevato.current = true;
    try {
      if (navigator.vibrate) navigator.vibrate(80);
    } catch (e) {
      // vibrazione non disponibile: ignorata
    }
    onRilevatoRef.current(pulito);
  };

  // Esc chiude lo scanner (senza chiudere eventuali modal sotto)
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        onChiudiRef.current();
      }
    };
    window.addEventListener('keydown', h, true);
    return () => window.removeEventListener('keydown', h, true);
  }, []);

  // blocca lo scroll della pagina sotto
  useEffect(() => {
    const sblocca = bloccaScroll();
    return () => sblocca();
  }, []);

  // avvio della fotocamera (e riavvio quando cambia il modo o si preme "Riprova")
  useEffect(() => {
    let annullato = false;
    setErrore(null);
    setAvvio(true);

    accoda(async () => {
      if (annullato) return;

      if (!lettoreRef.current) {
        lettoreRef.current = new Html5Qrcode(idLettore, {
          formatsToSupport: formatiDaNomi(formatiRef.current),
          verbose: false,
          // usa il BarcodeDetector nativo quando il browser lo ha (più veloce e più preciso)
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        });
      }
      const lettore = lettoreRef.current;

      // se era già attiva un'altra fotocamera, la ferma e aspetta che il telefono la rilasci
      if (lettore.isScanning) {
        await lettore.stop();
        await attesa(350);
        if (annullato) return;
      }

      const avvia = (modoDaUsare) => lettore.start(
        { facingMode: modoDaUsare },
        { fps: 10, videoConstraints: vincoliVideo(modoDaUsare) },
        (testo) => consegna(testo),
        () => {}, // frame senza codice: normale
      );

      try {
        try {
          await avvia(modo);
        } catch (primoErrore) {
          // la fotocamera posteriore può non esistere (es. PC): si ripiega sulla frontale
          const nome = primoErrore?.name || '';
          if (modo === 'environment' && (nome === 'OverconstrainedError' || nome === 'NotFoundError' || /constraint/i.test(String(primoErrore)))) {
            await attesa(250);
            await avvia('user');
            if (!annullato) setModo('user');
          } else {
            throw primoErrore;
          }
        }

        if (annullato) {
          // il componente è stato chiuso mentre la fotocamera partiva: la ferma subito
          if (lettore.isScanning) await lettore.stop();
          return;
        }
        setAvvio(false);

        // Quante fotocamere ci sono? enumerateDevices NON apre nessun flusso video
        // (a differenza di Html5Qrcode.getCameras, che su iPhone interrompe la fotocamera in uso).
        try {
          const dispositivi = await navigator.mediaDevices.enumerateDevices();
          if (!annullato) setPiuFotocamere(dispositivi.filter((d) => d.kind === 'videoinput').length > 1);
        } catch (e) {
          // elenco non disponibile: il pulsante "Cambia" resta com'è
        }
      } catch (e) {
        if (!annullato) {
          setAvvio(false);
          setErrore(messaggioErrore(e));
        }
      }
    });

    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, tentativo, idLettore]);

  // chiusura: ferma la fotocamera e libera l'elemento (in coda, dopo l'eventuale avvio in corso)
  useEffect(() => () => {
    accoda(async () => {
      const lettore = lettoreRef.current;
      lettoreRef.current = null;
      if (!lettore) return;
      try {
        if (lettore.isScanning) await lettore.stop();
        lettore.clear();
      } catch (e) {
        // già fermato
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cambiaFotocamera = () => {
    if (avvio) return;
    setModo((attuale) => (attuale === 'environment' ? 'user' : 'environment'));
  };

  const invia = (e) => {
    e.preventDefault();
    // lo scanner può essere aperto da dentro un altro form: l'invio non deve risalire al form esterno
    e.stopPropagation();
    consegna(manuale);
  };

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center p-3 sm:p-6" role="dialog" aria-modal="true" aria-label={titolo}>
      <style>{`
        @keyframes orizon-scan-linea { 0% { top: 8%; } 50% { top: 92%; } 100% { top: 8%; } }
        @keyframes orizon-scan-pulsa { 0%, 100% { opacity: .55; } 50% { opacity: 1; } }
      `}</style>

      <div className="absolute inset-0 bg-black/75 backdrop-blur-md animate-in fade-in duration-200" onClick={() => onChiudiRef.current()} />

      <div className="relative w-full max-w-md bg-card border border-border/60 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[94dvh] animate-in zoom-in-95 duration-300">
        <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/60 to-transparent shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-4 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
              <Icon name="ScanBarcode" size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-[9px] font-black uppercase tracking-[.3em] text-muted-foreground/50">Scanner</p>
              <h3 className="text-base font-black uppercase tracking-tight leading-tight">{titolo}</h3>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChiudiRef.current()}
            aria-label="Chiudi scanner"
            className="shrink-0 w-10 h-10 rounded-2xl bg-muted/50 hover:bg-red-500/10 border border-border/40 hover:border-red-500/20 text-muted-foreground hover:text-red-500 flex items-center justify-center transition-all"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Area fotocamera */}
        <div className="px-6 shrink-0">
          <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden bg-black border border-border/40">
            {/* contenitore del video (gestito da html5-qrcode) */}
            <div
              id={idLettore}
              className="absolute inset-0 [&_video]:!w-full [&_video]:!h-full [&_video]:object-cover"
            />

            {!errore && (
              <>
                {/* cornice con angoli */}
                <div className="pointer-events-none absolute inset-[12%] sm:inset-x-[10%] sm:inset-y-[18%]">
                  <span className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-2xl" />
                  <span className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-2xl" />
                  <span className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-2xl" />
                  <span className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-2xl" />
                </div>

                {/* linea rossa dello scanner */}
                {!avvio && (
                  <div className="pointer-events-none absolute inset-x-[8%] inset-y-0">
                    <div
                      className="absolute left-0 right-0 h-[3px] rounded-full bg-red-500 shadow-[0_0_14px_3px_rgba(239,68,68,.85)]"
                      style={{ animation: 'orizon-scan-linea 2.2s ease-in-out infinite' }}
                    />
                  </div>
                )}
              </>
            )}

            {avvio && !errore && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 text-white">
                <div className="w-9 h-9 border-4 border-white/20 border-t-white rounded-full animate-spin" />
                <span className="text-[10px] font-black uppercase tracking-[.3em]" style={{ animation: 'orizon-scan-pulsa 1.4s ease-in-out infinite' }}>
                  Avvio fotocamera...
                </span>
              </div>
            )}

            {errore && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center bg-black/85 text-white">
                <Icon name="CameraOff" size={36} className="text-white/60" />
                <p className="text-[12px] font-bold leading-relaxed text-white/80">{errore}</p>
                <button
                  type="button"
                  onClick={() => { setModo('environment'); setTentativo((t) => t + 1); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-[10px] font-black uppercase tracking-widest transition-all"
                >
                  <Icon name="RotateCcw" size={12} />
                  Riprova
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 mt-3">
            <p className="text-[10px] font-bold text-muted-foreground/60 leading-snug">
              Inquadra il codice a barre e tienilo dentro la cornice.
              {piuFotocamere && (
                <span className="block mt-0.5 text-muted-foreground/40">
                  Fotocamera: {modo === 'environment' ? 'posteriore' : 'frontale'}
                </span>
              )}
            </p>
            {piuFotocamere && (
              <button
                type="button"
                onClick={cambiaFotocamera}
                disabled={avvio}
                className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border/40 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all disabled:opacity-50"
              >
                <Icon name="SwitchCamera" size={13} />
                Cambia
              </button>
            )}
          </div>
        </div>

        {/* Inserimento manuale */}
        <form onSubmit={invia} className="p-6 pt-4 shrink-0">
          <label htmlFor={`${idLettore}-manuale`} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/60 px-1 mb-1.5">
            <Icon name="Keyboard" size={12} />
            Oppure digita il codice
          </label>
          <div className="flex gap-2">
            <input
              id={`${idLettore}-manuale`}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={manuale}
              onChange={(e) => setManuale(e.target.value)}
              placeholder="es. 2024506012345"
              className="flex-1 min-w-0 rounded-2xl px-4 py-3 text-sm font-bold font-mono outline-none transition-all bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60"
            />
            <button
              type="submit"
              disabled={manuale.trim() === ''}
              className="shrink-0 px-5 py-3 bg-primary text-primary-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.15em] hover:opacity-90 transition-all shadow-lg shadow-primary/20 disabled:opacity-40"
            >
              Invio
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BarcodeScanner;
